import { useSelector } from 'react-redux'

import {
  CurrencyNames,
  type CurrencyName,
} from '@webapp/core/entities/external/Currency'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import {
  FinancialAccountStatuses,
  type FinancialAccountStatusEnum,
} from '@webapp/platform/banking/entities/enums/FinancialAccountStatusEnum'
import {
  FinancialAccountTypes,
  type FinancialAccountTypeEnum,
} from '@webapp/platform/banking/entities/enums/FinancialAccountTypeEnum'
import { useQueryFinancialAccount } from '@webapp/platform/banking/features/get-financial-account/models/useQueryFinancialAccount'
import { type PaymentSource } from '@webapp/utils/payment-source'

type UseFinancialAccountStateReturn = {
  isFinancialAccountEnabled: boolean
  isLoadingFinancialAccountSetupState: boolean
  isFinancialAccountSetUp: boolean
  financialAccountId: PaymentSource['id']
  financialAccountStatus: FinancialAccountStatusEnum
  financialAccountType: FinancialAccountTypeEnum
  financialAccountCurrency: CurrencyName
}

export const useFinancialAccountState = ({
  enabled = true,
}: { enabled?: boolean } = {}): UseFinancialAccountStateReturn => {
  const { bill_pay, enable_airwallex_onboarding, enable_airwallex_issuing } =
    useSelector(makeSelectFeatureSwitches())

  const isFinancialAccountEnabled =
    bill_pay || enable_airwallex_onboarding || enable_airwallex_issuing

  const { financialAccount, isFetching } = useQueryFinancialAccount({
    onError: () => null,
    enabled: enabled && isFinancialAccountEnabled,
  })

  const getCurrency = (): CurrencyName => {
    if (!financialAccount) return null

    switch (financialAccount.type) {
      case FinancialAccountTypes.AIRWALLEX_USD:
        return CurrencyNames.USD
      case FinancialAccountTypes.AIRWALLEX_CAD:
        return CurrencyNames.CAD
      case FinancialAccountTypes.AIRWALLEX_GBP:
        return CurrencyNames.GBP
      default:
        return null
    }
  }

  return {
    isFinancialAccountEnabled,
    isLoadingFinancialAccountSetupState: isFetching,
    isFinancialAccountSetUp:
      financialAccount?.status === FinancialAccountStatuses.ACTIVE || false,
    financialAccountId: financialAccount?.id || null,
    financialAccountStatus: financialAccount?.status || null,
    financialAccountType: financialAccount?.type || null,
    financialAccountCurrency: getCurrency(),
  }
}
